import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    Box,
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import { DATA_GRID_LOCALE_TEXT, DATA_GRID_SX } from "../../../../components/ui/dataGridStyles";
import InfoOrder from "../ui/InfoOrder";
import OrderProduct from "./OrderProduct";
import ProductosMobileList from "./ProductosMobileList";
import Utils from "../../../../utils/Utils";
import { FormularioPuntear, FiltrosProductos } from "../forms";
import Toasts from "../../../../components/notificacion/Toasts";
import OrderService from "../../../../services/order.service";
import VoiceUtils from "../../../../utils/VoiceUtils";
import {ORDER_STATE, ORDER_TYPE} from "../../../../config/constants";
import {StateContext} from "../../../../components/context/StateProvider";
import successSound from "../../../../assets/audio/success.mp3";
import errorSound from "../../../../assets/audio/error.mp3";
import warningSound from "../../../../assets/audio/warning.mp3";
import completeSound from "../../../../assets/audio/complete.mp3";


/**
 * Buscar todos los productos que correspondan con un ean
 */
const searchProductByEan = (state, eanRef) => {
    return state.products.map((product, index) => {
        const eanIndex = product.ean.findIndex((ean) => ean.BARRAS === eanRef);
        return eanIndex !== -1 ? {'index': index, 'units': product.ean[eanIndex].UNIDADES} : {
            'index': -1,
            'units': 0
        };
    }).filter((product) => product.index !== -1);
};

/**
 * Definición columnas de la tabla
 */
const generateColumns = (read) => {
    const columns = [
        {
            field: 'ref',
            headerName: 'Referencia',
            flex: 1,
        },
        {
            field: 'ean',
            headerName: 'Cod. Barras',
            flex: 1,
        },
        {
            field: 'name',
            headerName: 'Descripción',
            flex: 1,
        },
        {
            field: 'units',
            headerName: 'Unidades',
            flex: 1,
        },
        {
            field: 'location',
            headerName: 'Ubicación',
            flex: 1,
        },
        {
            field: 'state',
            headerName: 'Estado',
            flex: 1,
        },
        {
            field: 'serie',
            headerName: 'Serie',
            flex: 1,
        },
    ];

    if (!read) {
        columns.push({
            field: 'registerDate',
            headerName: 'Registrado',
            flex: 1,
        });
    }

    return columns;
};

const ListOrder = (props) => {

    const contexto = useContext(StateContext);
    const {state} = contexto;

    // Replica lo que hacía `this.context` dentro de un método de clase: leer el valor
    // ACTUAL del contexto en el momento de la llamada, no el capturado en el render en
    // que se creó el callback. Importa para acumular toasts sin perder los anteriores.
    const contextoRef = useRef(contexto);
    contextoRef.current = contexto;

    const eanRef = useRef(null);
    const unitsRef = useRef(null);
    const infoOrderRef = useRef(null);
    const autoFocusEanDoneRef = useRef(false);

    // componentDidUpdate no corre en el montaje; este flag replica esa diferencia
    const esPrimerRenderRef = useRef(true);
    // Equivalente a prevProps.order?.current
    const ordenPreviaRef = useRef(null);

    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 900px)').matches);

    const {
        type, handle, registerAction, read, preparer, reviewer, camera, orderStateN2,
        autoAssignReviewer = true, onAssignToOrder, hideInfoOrder, permitirImagenesCompleto,
        autoFocusEan, nave,
    } = props;

    /**
     * Llevar el foco al campo de escaneo cuando la página lo pide con autoFocusEan.
     * Pensado para retomar un punteo ya empezado: se entra y se puede escanear sin
     * tocar la pantalla. Una sola vez por montaje, para no robar el foco después de
     * cada punteo (de eso ya se encarga el efecto de actualización).
     */
    const maybeAutoFocusEan = useCallback(() => {
        if (!autoFocusEan || autoFocusEanDoneRef.current || !eanRef.current) {
            return;
        }

        autoFocusEanDoneRef.current = true;
        // preventScroll: el focus haría su propio salto y competiría con el scroll suave
        eanRef.current.focus({ preventScroll: true });
        eanRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [autoFocusEan]);

    /**
     * Actualizar pedido
     */
    const updateOrder = useCallback(async (service, state, index, refArticulo, unitsRegister, registerDate, units, updateState, manual, year) => {
        await service(
            state.products[index].order,
            refArticulo,
            unitsRegister,
            registerDate,
            manual,
            year,
            nave,
            type
        ).then(
            (data) => {
                state.products[index].remaining = state.products[index].remaining - units;
                if (state.products[index].remaining === 0) {
                    state.products[index].state = 1;
                }

                if (!state.products.some(item => item.remaining > 0)) {

                    updateState({filters: []})

                    // Si es movil suena un sonido sino habla
                    if (Utils.isMobile()) {
                        Utils.playAudio(completeSound);
                    } else {
                        VoiceUtils.speak('Pedido completado');
                    }

                    // Solo cambiar estado automáticamente si no hay callback personalizado (grupos)
                    const shouldAutoChangeState = !onAssignToOrder;

                    if (shouldAutoChangeState && state.order.n2 && ((!orderStateN2 && state.order.stateN2 !== '3') || (orderStateN2 && state.order.state !== '3') )) {
                        // Cambiar el estado de la orden en nave 2 si se completa la orden y no se ha cambiado el estado de la nave 2 a preparado
                        OrderService.assignToOrder(state.order.num, null, null, handle, '3', state.order.n2, undefined, year, null, type, null, null).then(
                            () => {
                                if (orderStateN2) {
                                    state.order.state = '3';
                                } else {
                                    state.order.stateN2 = '3';
                                }
                                updateState({order: state.order});
                            }
                        ).catch((error) => {
                            const toasts = Toasts.generateToast(error.response?.data?.message, 'error');
                            updateState({toasts: [...state.toasts, toasts]})
                        });
                    }

                    if (shouldAutoChangeState && handle === 'ventas' && !orderStateN2) {
                        // Cambiar estado de pedido
                        OrderService.assignToOrder(state.order.num, null, null, handle, '3', null, undefined, state.year, null, type, null, null).then(
                            () => {
                                state.order.state = '3';
                                state.order.complete = true;
                                updateState({order: state.order});
                            }
                        ).catch((error) => {
                            const toasts = Toasts.generateToast(error.response?.data?.message, 'error');
                            updateState({toasts: [...state.toasts, toasts]})
                        });
                    }
                }

                state.products[index].register = unitsRegister;
                state.products[index].manual = manual;
                state.products[index].registerDate = Utils.formatDate(registerDate);
                state.order.setLastUpdated(registerDate);
                const toasts = Toasts.generateToast('Producto escaneado: <strong>ubicación = ' + state.products[index].location + '</strong>' + (state.products[index].serie ? ' // <strong class="text-danger">Producto con serie</strong>' : ''), 'success');
                updateState({toasts: [...state.toasts, toasts]})
                Utils.playAudio(successSound);
            },
            (error) => {
                const resMessage =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString();

                const toasts = Toasts.generateToast(resMessage, 'success');
                updateState({toasts: [...state.toasts, toasts]})
                Utils.playAudio(errorSound);
            })
    }, [nave, type, handle, orderStateN2, onAssignToOrder]);

    /**
     * Gestionar añadir productos a lista de ventas o de compras
     */
    const handleProductOrder = useCallback(async (context, eanValue, unitsValue, service) => {

        const {state, updateState} = context;

        // Buscar todos los productos que tengan un codigo de barras
        const indexes = searchProductByEan(state, eanValue)

        if (indexes.length > 0) {

            for (let i = 0; i < indexes.length; i++) {

                let index = indexes[i].index;
                let units = (indexes[i].units > 1 ? indexes[i].units : unitsValue);

                const unitsRegister = state.products[index].register + units;

                if (state.products[index].remaining > 0 && state.products[index].units >= unitsRegister) {

                    let refArticulo = state.products[index].ref;

                    if (state.products[index].duplicate) {
                        refArticulo += '-' + state.products[index].line;
                    }

                    // TODO añadir al servicio de registar como en bultos
                    let registerDate = new Date();
                    await updateOrder(service, state, index, refArticulo, unitsRegister, registerDate, units, updateState, false, state.year);

                    break;

                } else if (state.products[index].remaining === 0 && i === indexes.length - 1) {
                    const toasts = Toasts.generateToast('Ya se han añadido todos los productos', 'warning');
                    updateState({toasts: [...state.toasts, toasts]})
                    Utils.playAudio(warningSound);
                } else if (unitsRegister > state.products[index].units && i === indexes.length - 1) {
                    const toasts = Toasts.generateToast('No puede registrar tantas unidades del producto', 'warning');
                    updateState({toasts: [...state.toasts, toasts]})
                    Utils.playAudio(warningSound);
                }
            }

        } else {
            const toasts = Toasts.generateToast('No existe producto en el pedido', 'error');
            updateState({toasts: [...state.toasts, toasts]})
            Utils.playAudio(errorSound);
        }
    }, [updateOrder]);

    /**
     * Manejar submit
     */
    const handleSubmit = async (event, service) => {

        if (event) {
            event.preventDefault();
        }

        if (eanRef.current.value !== "") {

            // Añadir productos
            await handleProductOrder(contextoRef.current, eanRef.current.value, unitsRef.current.valueAsNumber, service);

            eanRef.current.value = '';
            unitsRef.current.value = 1;
        }

        eanRef.current.focus();
    };

    /**
     * Forzar punteo desde vista móvil
     * @param producto - Producto a forzar
     */
    const handleForceCheckMobile = useCallback(async (producto) => {
        const { state, updateState } = contextoRef.current;

        const unitsRegister = producto.register + producto.remaining;
        let refArticulo = producto.ref;

        if (producto.duplicate) {
            refArticulo += '-' + producto.line;
        }

        await updateOrder(
            registerAction,
            state,
            producto.id,
            refArticulo,
            unitsRegister,
            new Date(),
            producto.remaining,
            updateState,
            true, // manual = true para forzar
            state.year
        );
    }, [updateOrder, registerAction]);

    const filterList = (value) => {
        const {updateState} = contextoRef.current;
        // DECISION: Normalizar a string porque el Select de MUI conserva el tipo numérico de ProductStates
        if (String(value) === '3') {
            updateState({filters: []})
        } else {
            updateState({filters: [{field: 'state', operator: 'equals', value: (parseInt(value) - 1).toString()}]})
        }
    };

    /**
     * Determinar si se pueden mostrar los productos (asignaciones obligatorias del flujo completas)
     */
    const canShowProducts = (
        (state.order.reviewer !== '0' && handle === 'compras') ||
        (state.order.preparer !== '0' && state.order.reviewer !== '0' && ((camera && state.order.camera !== null && state.order.camera !== '') || !camera) && handle === 'ventas') ||
        (state.order.complete && (type !== ORDER_TYPE.PEDIDO.valueOf() || state.order.state !== ORDER_STATE.PREPARADO || orderStateN2)) ||
        (orderStateN2 && state.order.preparer !== '0' && state.order.state !== '0' && handle === 'compras') ||
        (orderStateN2 && state.order.preparer !== '0' && state.order.state !== '0' && ((camera && state.order.camera !== null && state.order.camera !== '') || !camera) && handle === 'ventas')
    );

    // Montaje y desmontaje: listener de media query, auto-foco inicial y limpieza
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 900px)');
        const handleMediaQueryChange = (e) => {
            setIsMobile(e.matches);
        };
        // Listener moderno
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleMediaQueryChange);
        } else {
            // Fallback para navegadores antiguos
            mediaQuery.addListener(handleMediaQueryChange);
        }

        maybeAutoFocusEan();

        return () => {
            // Remover listener del media query
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleMediaQueryChange);
            } else {
                // Fallback para navegadores antiguos
                mediaQuery.removeListener(handleMediaQueryChange);
            }
            contextoRef.current.updateState({filters: []});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Equivalente a componentDidUpdate: corre tras cada render salvo el primero
    useEffect(() => {
        const ordenPrevia = ordenPreviaRef.current;
        ordenPreviaRef.current = props.order?.current ?? null;

        if (esPrimerRenderRef.current) {
            esPrimerRenderRef.current = false;
            return;
        }

        // Los productos llegan de forma asíncrona, así que el formulario no existe aún
        // en el montaje: hay que reintentarlo cuando aparece
        maybeAutoFocusEan();

        // DECISION: hacer scroll al InfoOrder solo mientras faltan asignaciones obligatorias
        // (productos ocultos). Comprobar campos sueltos hacía scroll en cada punteo en flujos
        // donde preparador/cámara no aplican (p.ej. compras).
        // No hacer scroll si InfoOrder está oculto (grupos)
        if (!canShowProducts && !hideInfoOrder) {
            infoOrderRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        } else if (props.order?.current && ordenPrevia
            && ordenPrevia.value === props.order.current.value
            && props.order.current.value !== ''
            && state.order.editing === false) {
            // Devolver el foco al campo EAN tras cada punteo (solo en vistas con buscador propio)
            eanRef.current?.focus();
        }
    });

    const columns = generateColumns(read);

    return (
        <Box>
            {state.products.length > 0 && (
                <Box>
                    {/* InfoOrder - Oculto cuando hideInfoOrder es true (grupos) */}
                    {!hideInfoOrder && (
                        <Box ref={infoOrderRef}>
                            <InfoOrder
                                type={type}
                                order={handle}
                                read={read}
                                preparer={preparer}
                                reviewer={reviewer}
                                orderStateN2={orderStateN2}
                                camera={camera}
                                autoAssignReviewer={autoAssignReviewer}
                                onAssignToOrder={onAssignToOrder}
                                permitirImagenesCompleto={permitirImagenesCompleto}
                            />
                        </Box>
                    )}

                    {canShowProducts && (
                        <>
                            {/* Formulario de puntear */}
                            {!state.order.complete && !read && (
                                <FormularioPuntear
                                    type={type}
                                    unitsRef={unitsRef}
                                    eanRef={eanRef}
                                    onSubmit={(e) => handleSubmit(e, registerAction)}
                                />
                            )}

                            {/* Lista de productos */}
                            <Box id='list-order' sx={{ mt: 3 }}>
                                {!read && !state.order.complete && (
                                    <FiltrosProductos
                                        productStates={true}
                                        filter={(value) => filterList(value)}
                                    />
                                )}

                                {/* Vista móvil vs desktop */}
                                {isMobile ? (
                                    <ProductosMobileList
                                        productos={state.products.filter(p => {
                                            if (state.filters.length === 0) return true;
                                            const filter = state.filters[0];
                                            return String(p[filter.field]) === filter.value;
                                        })}
                                        read={read}
                                        orderComplete={state.order.complete}
                                        onForceCheck={handleForceCheckMobile}
                                    />
                                ) : (
                                    <DataGrid
                                        rows={state.products}
                                        columns={columns}
                                        localeText={DATA_GRID_LOCALE_TEXT}
                                        sx={DATA_GRID_SX}
                                        disableRowSelectionOnClick={true}
                                        pagination={false}
                                        hideFooterPagination
                                        filterModel={{
                                            items: state.filters
                                        }}
                                        initialState={{
                                            columns: {
                                                columnVisibilityModel: {
                                                    state: false,
                                                    serie: false
                                                },
                                            },
                                        }}
                                        slots={{
                                            row: OrderProduct,
                                        }}
                                        slotProps={{
                                            row: {
                                                read: read,
                                                updateOrderAction: updateOrder,
                                                registerOrderAction: registerAction
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ListOrder;
